import { computed, ComputedRef, Ref, ref } from 'vue'
import { defineStore } from 'pinia'

import { BaseError, objectify } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'

import {
    AssignTagParams,
    CreateTagParams,
    entityKey,
    EntityWithTags,
    FetchTagsParams,
    Tag,
    TaggableType,
    UnassignTagParams,
    UpdateTagParams
} from './types'

/**
 * Routes API consommées :
 *   GET    /v1/tags?scope=agency&agency_id=:id&pagination=false
 *   GET    /v1/tags/:id
 *   POST   /v1/tags
 *   PUT    /v1/tags/:id
 *   DELETE /v1/tags/:id
 *   POST   /v1/tags/:id/assign      body: { taggable_type, taggable_id }
 *   DELETE /v1/tags/:id/unassign    body: { taggable_type, taggable_id }
 *
 * Les tags d'une entité sont sérialisés automatiquement dans les réponses
 * des stores parents (clients, employees, cards, agencies, organizations, users).
 * Ces stores appellent ingestTaggingsFromEntities pour peupler taggingsByEntity.
 */
export const useTagsStore = defineStore('tags', () => {
    const { api } = useEzioAPI()

    const all: Ref<{ [id: string]: Tag }> = ref({})
    const taggingsByEntity: Ref<{ [key: string]: Tag[] }> = ref({})

    const normalize = (tag: Tag): Tag =>
        Object.assign({}, tag, {
            id: tag.id != null ? String(tag.id) : tag.id,
            agencyId: tag.agencyId != null ? String(tag.agencyId) : undefined
        })

    const agencyTags: ComputedRef<Tag[]> = computed(() =>
        Object.values(all.value).filter((t) => t.scope === 'agency')
    )

    const systemTags: ComputedRef<Tag[]> = computed(() =>
        Object.values(all.value).filter((t) => t.scope === 'system')
    )

    const fetchCollection = async (params: FetchTagsParams = {}) => {
        try {
            const payload = Object.assign({}, params, { pagination: false })
            const response = await api.get('tags', payload)
            store(response.data)
            return response.data as Tag[]
        } catch (error) {
            //
        }
    }

    const fetch = async (tagId: string) => {
        try {
            const response = await api.get('tags/' + tagId)
            store([response.data])
            return response.data as Tag
        } catch (error) {
            //
        }
    }

    const create = async (data: CreateTagParams) => {
        try {
            const response = await api.post('tags', data)
            store([response.data])
            return response.data as Tag
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const update = async (data: UpdateTagParams) => {
        try {
            const response = await api.put('tags/' + data.id, data)
            store([response.data])
            return response.data as Tag
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const remove = async (tagId: string) => {
        try {
            await api.delete('tags/' + tagId)
            const next = Object.assign({}, all.value)
            delete next[tagId]
            all.value = next
            for (const key of Object.keys(taggingsByEntity.value)) {
                taggingsByEntity.value[key] = taggingsByEntity.value[key].filter(
                    (t) => t.id !== tagId
                )
            }
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const assign = async (params: AssignTagParams) => {
        try {
            await api.post('tags/' + params.tag_id + '/assign', {
                taggable_type: params.taggable_type,
                taggable_id: params.taggable_id
            })
            const tag = all.value[String(params.tag_id)]
            const current = getTagsForEntity(params.taggable_type, params.taggable_id)
            const next =
                tag && !current.some((t) => t.id === tag.id) ? [...current, tag] : current
            indexEntity(params.taggable_type, params.taggable_id, next)
            return next
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const unassign = async (params: UnassignTagParams) => {
        try {
            await api.delete('tags/' + params.tag_id + '/unassign', {
                taggable_type: params.taggable_type,
                taggable_id: params.taggable_id
            })
            const current = getTagsForEntity(params.taggable_type, params.taggable_id)
            const next = current.filter((t) => t.id !== String(params.tag_id))
            indexEntity(params.taggable_type, params.taggable_id, next)
            return next
        } catch (error: any) {
            throw new BaseError({ title: 'Oups !', message: error.message })
        }
    }

    const getTagsForEntity = (taggableType: TaggableType, taggableId: string): Tag[] => {
        if (!taggableId) return []
        return taggingsByEntity.value[entityKey(taggableType, taggableId)] ?? []
    }

    const indexEntity = (
        taggableType: TaggableType,
        taggableId: string,
        tags: Tag[]
    ) => {
        const normalized = (tags ?? []).map(normalize)
        taggingsByEntity.value = Object.assign({}, taggingsByEntity.value, {
            [entityKey(taggableType, taggableId)]: normalized
        })
        if (normalized.length) store(normalized)
    }

    const ingestTaggingsFromEntities = (
        taggableType: TaggableType,
        entities: EntityWithTags[]
    ) => {
        const next = Object.assign({}, taggingsByEntity.value)
        for (const entity of entities ?? []) {
            if (!entity?.id) continue
            const e: any = entity
            const agency = Array.isArray(e.tags) ? e.tags : []
            const system = Array.isArray(e.systemTags) ? e.systemTags : []
            if (agency.length || system.length || Array.isArray(e.tags) || Array.isArray(e.systemTags)) {
                const merged = [
                    ...agency.map((t: Tag) => normalize({ ...t, scope: t.scope ?? 'agency' })),
                    ...system.map((t: Tag) => normalize({ ...t, scope: 'system' }))
                ]
                next[entityKey(taggableType, entity.id)] = merged
                if (merged.length) store(merged)
            }
        }
        taggingsByEntity.value = next
    }

    const store = (tags: Tag[]) => {
        all.value = Object.assign({}, all.value, objectify((tags ?? []).map(normalize)))
    }

    const reset = () => {
        all.value = {}
        taggingsByEntity.value = {}
    }

    return {
        all,
        taggingsByEntity,
        agencyTags,
        systemTags,
        fetchCollection,
        fetch,
        create,
        update,
        remove,
        assign,
        unassign,
        getTagsForEntity,
        indexEntity,
        ingestTaggingsFromEntities,
        reset
    }
})
